// ════════════════════════════════════════════════════════════════════
// COMMISSION LEDGER — UHC statement commit path.
//
// Carrier statements aren't policy imports. They're transactions
// against EXISTING policies. So we don't run them through importPlanner
// (which is built for client+policy upserts). Instead we:
//
//   1. Open a commission_statements row (one per uploaded file).
//   2. For each parsed line:
//      a. Resolve the matching health_policies.id via (carrier +
//         member_id) — the same Rule-1 key the dedupe matcher uses.
//      b. Insert a commission_transactions row tied to that policy.
//      c. Side effects per action:
//         · NEW           → bump policy.last_commission_at + status='active'
//         · PMPM          → bump policy.last_commission_at
//         · CHARGEBACK    → fire health_loss_events (payment_stopped)
//         · REINSTATEMENT → status='active'
//         · ADJUSTMENT    → no-op on policy
//      d. Recompute policy.health_score (0-100 rolling band).
//   3. Stamp the statement totals + close.
//
// Returns { statementId, counts, errors[] }.
//
// MULTI-TENANCY GUARANTEE
// ──────────────────────────────────────────────────────────────────
// Every insert/update carries owner_id and runs against the
// RLS-scoped supabase client. The (member_id + carrier) resolver
// only queries health_policies WHERE owner_id = current operator.
// ════════════════════════════════════════════════════════════════════

import { normalizeCarrier } from './clientMatcher';

// ── Score per transaction action (most recent wins) ──
//   100 = active with PMPM ≤ 45d
//    80 = new enrollment, no PMPM yet
//    50 = no PMPM 45-60d
//    25 = 1 chargeback in last 90d
//     0 = 2+ chargebacks OR explicit termination
function scoreForRow({ action, recentChargebackCount, daysSinceLastPmpm }) {
  if (action === 'chargeback') return recentChargebackCount >= 2 ? 0 : 25;
  if (action === 'new')        return 80;
  if (action === 'pmpm')       {
    if (daysSinceLastPmpm == null || daysSinceLastPmpm <= 45) return 100;
    if (daysSinceLastPmpm <= 60) return 50;
    return 25;
  }
  if (action === 'reinstatement') return 80;
  return null;
}

// Load every active policy for this owner into an in-memory index
// keyed by (carrier-normalized + lower(member_id)). One round trip
// up front beats N+1 lookups per row.
async function loadPolicyIndex(supabase, ownerId) {
  const { data } = await supabase
    .from('health_policies')
    .select('id, carrier, member_id, policy_number, status, last_commission_at, monthly_commission, client_name, plan_type')
    .eq('owner_id', ownerId);
  const byMemberCarrier = new Map();
  const byPolicyCarrier = new Map();
  for (const p of data || []) {
    if (p.member_id && p.carrier) {
      byMemberCarrier.set(`${normalizeCarrier(p.carrier)}::${p.member_id.toLowerCase()}`, p);
    }
    if (p.policy_number && p.carrier) {
      byPolicyCarrier.set(`${normalizeCarrier(p.carrier)}::${p.policy_number.toLowerCase()}`, p);
    }
  }
  return { byMemberCarrier, byPolicyCarrier };
}

function resolvePolicy(row, index) {
  const cn = normalizeCarrier(row.carrier || 'UnitedHealthcare');
  if (row.member_id) {
    const hit = index.byMemberCarrier.get(`${cn}::${row.member_id.toLowerCase()}`);
    if (hit) return hit;
  }
  if (row.policy_number) {
    const hit = index.byPolicyCarrier.get(`${cn}::${row.policy_number.toLowerCase()}`);
    if (hit) return hit;
  }
  return null;
}

// ── The committer ──────────────────────────────────────────────
export async function commitUhcStatement({ supabase, ownerId, parsed, fileName, currentUser }) {
  const { rows, summary, statementDate, carrier } = parsed;
  if (!rows || rows.length === 0) {
    return { statementId: null, counts: { written: 0, linked: 0, chargebacks: 0, lossEvents: 0 }, errors: [] };
  }

  const errors = [];

  // 1. Open statement row.
  const { data: statementRow, error: stErr } = await supabase
    .from('commission_statements')
    .insert([{
      owner_id:        ownerId,
      carrier:         carrier || 'UnitedHealthcare',
      carrier_short:   'UHC',
      statement_date:  statementDate,
      source_kind:     'uhc_csv',
      source_filename: fileName || null,
      total_rows:      summary.rowCount,
      new_count:           summary.byAction.new || 0,
      pmpm_count:          summary.byAction.pmpm || 0,
      chargeback_count:    summary.byAction.chargeback || 0,
      reinstatement_count: summary.byAction.reinstatement || 0,
      adjustment_count:    summary.byAction.adjustment || 0,
      gross_amount:        summary.grossAmount,
      chargeback_amount:   summary.chargebackAmount,
      net_amount:          summary.netAmount,
    }])
    .select('id')
    .single();
  if (stErr) {
    // If commission_statements is missing the operator hasn't run
    // migration 045. Surface a clean error instead of dying.
    const msg = String(stErr.message || '').toLowerCase();
    if (msg.includes('schema cache') || msg.includes('does not exist') || msg.includes('relation')) {
      throw new Error('Commission Intelligence schema missing — run migration 045 in Supabase first.');
    }
    throw stErr;
  }
  const statementId = statementRow.id;

  // 2. Resolve each parsed row to an existing policy.
  const index = await loadPolicyIndex(supabase, ownerId);

  const txInserts = [];
  const lossInserts = [];
  const policyUpdates = new Map();  // policy_id → patch
  let linked = 0;
  let chargebacks = 0;

  // Count recent chargebacks per policy for the health-score calc.
  const recentChargebacks = new Map();
  for (const r of rows) {
    if (r.action === 'chargeback') {
      const p = resolvePolicy(r, index);
      if (p) recentChargebacks.set(p.id, (recentChargebacks.get(p.id) || 0) + 1);
    }
  }

  for (const r of rows) {
    const policy = resolvePolicy(r, index);
    if (policy) linked++;

    txInserts.push({
      owner_id:       ownerId,
      statement_id:   statementId,
      policy_id:      policy?.id || null,
      client_name:    r.client_name,
      member_id:      r.member_id,
      policy_number:  r.policy_number,
      carrier:        r.carrier,
      carrier_short:  'UHC',
      product_code:   r.product_code,
      product_label:  r.product_label,
      state:          r.state,
      effective_date: r.effective_date,
      agent_npn:      r.agent_npn,
      action:         r.action,
      amount:         r.amount ?? 0,
      paid_date:      r.paid_date || statementDate,
      raw:            r._raw || {},
    });

    if (!policy) continue;
    const existing = policyUpdates.get(policy.id) || {};

    // PMPM / NEW → bump last_commission_at + revive status.
    if (r.action === 'pmpm' || r.action === 'new') {
      const d = r.paid_date || statementDate;
      if (d && (!existing.last_commission_at || d > existing.last_commission_at)) {
        existing.last_commission_at = d;
      }
      if (r.action === 'new') existing.status = 'active';
    }
    // REINSTATEMENT → restore active.
    if (r.action === 'reinstatement') {
      existing.status = 'active';
    }
    // CHARGEBACK → fire loss event (one per policy per statement, not per row).
    if (r.action === 'chargeback' && !existing._chargebackFired) {
      existing._chargebackFired = true;
      chargebacks++;
      const lostAt = r.paid_date || statementDate || new Date().toISOString().slice(0, 10);
      lossInserts.push({
        owner_id:           ownerId,
        policy_id:          policy.id,
        client_name:        policy.client_name || r.client_name,
        plan_name:          null,
        plan_type:          policy.plan_type,
        monthly_commission: Math.abs(Number(r.amount) || 0),
        lost_at:            lostAt,
        reason:             'payment_stopped',
        notes:              `Auto-filed from UHC statement (${statementDate || 'undated'}) — chargeback ${r.amount < 0 ? `-$${Math.abs(r.amount).toFixed(2)}` : `$${r.amount?.toFixed(2) || '0'}`}.`,
      });
    }

    // Health score — last write wins per policy.
    const recentCb = recentChargebacks.get(policy.id) || 0;
    const daysSinceLastPmpm = policy.last_commission_at
      ? Math.floor((new Date() - new Date(policy.last_commission_at)) / (24 * 3600 * 1000))
      : null;
    const score = scoreForRow({ action: r.action, recentChargebackCount: recentCb, daysSinceLastPmpm });
    if (score != null) {
      existing.health_score = score;
      existing.health_score_updated = new Date().toISOString();
    }

    policyUpdates.set(policy.id, existing);
  }

  // 3. Batch the writes.
  if (txInserts.length > 0) {
    const { error: txErr } = await supabase.from('commission_transactions').insert(txInserts);
    if (txErr) errors.push(`transactions: ${txErr.message}`);
  }

  // Per-policy updates — done individually because each policy can have a different patch.
  for (const [policyId, patch] of policyUpdates.entries()) {
    const { _chargebackFired, ...write } = patch;
    if (Object.keys(write).length === 0) continue;
    const { error: upErr } = await supabase.from('health_policies').update(write).eq('id', policyId);
    if (upErr) errors.push(`policy ${policyId}: ${upErr.message}`);
  }

  // Loss events — dedupe constraint catches same-day repeats.
  let lossEvents = 0;
  for (const ev of lossInserts) {
    const { error: lossErr } = await supabase.from('health_loss_events').insert(ev);
    if (!lossErr) lossEvents++;
    else if (!/duplicate|unique/i.test(lossErr.message || '')) {
      errors.push(`loss event ${ev.client_name}: ${lossErr.message}`);
    }
  }

  return {
    statementId,
    counts: {
      written:     txInserts.length,
      linked,
      unresolved:  txInserts.length - linked,
      chargebacks,
      lossEvents,
      policyUpdates: policyUpdates.size,
    },
    errors,
  };
}
